import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { fetchGlobalBatchFiles, filterFiles, selectGlobalBatchFiles, selectLoading, selectPagination, selectTotal } from "../globalBatchFilesSlice"
import { regenerateQuickBookFile } from "../globalBatchSlice"
import { notification, Skeleton, Table, Tooltip } from "antd"
import { FileTextOutlined, SearchOutlined, SyncOutlined } from "@ant-design/icons"
import moment from "moment"
import { allObjectValuesEmpty } from "../../../functions/objectFunctions/allObjectValuesEmpty"
import ColumnHeaderTitle from "../../../components/utilityComponents/table/ColumnHeaderTitle"
import ColumnDateFilter from "../../../components/utilityComponents/table/ColumnDateFilter"
import { tableFiltersApplied } from "../../../functions/objectFunctions/tableFiltersApplied"
import ColumnSearchFilter from "../../../components/utilityComponents/table/ColumnSearchFilter"
import WarningChip from "../../bills/components/subcomponents/WarningChip"
import useExportConfirmationModal from "../../alertsPanel/ShowConfirmGlobalExport"

export default function FileTable({}) {
    // console.log('here')

    /////*** redux start ***/////
    const dispatch = useDispatch()
    const pagination = useSelector(selectPagination)
    const total = useSelector(selectTotal)
    const globalBatchFiles = useSelector(selectGlobalBatchFiles)
    const loading = useSelector(selectLoading)
    /////*** redux end ***/////

    /////*** useState start ***/////
    const [loaded, setLoaded] = useState(false)
    const [filters, setFilters] = useState({})
    const [sort, setSort] = useState({
        column: "created_at",
        direction: "desc",
    })
    /////*** useState end ***/////

    /////*** useEffect start ***/////
    useEffect(() => {
        if (!loaded) {
            setLoaded(true)
            handleFetchInitialFiles()
        }
    }, [loaded, dispatch, pagination]) // eslint-disable-line
    /////*** useEffect end ***/////

    const handleFetchInitialFiles = () => {
        dispatch(fetchGlobalBatchFiles(pagination, sort))
    }

    const handleTableChange = (newPagination, sorter) => {
        if (allObjectValuesEmpty(filters) && allObjectValuesEmpty(sorter)) {
            dispatch(fetchGlobalBatchFiles(newPagination))
        } else {
            handleFilterFiles(newPagination)
        }
    }

    const handleFilterFiles = (newPagination = null, newSort = null) => {
        // console.log(filters)
        let filterJson = {
            filters: filters,
            pagination: !!newPagination ? newPagination : pagination,
            sort: !!newSort ? newSort : sort,
        }
        return dispatch(
            filterFiles(filterJson)
        )
    }

    const handleSortBatchChange = (column) => () => {
        // console.log(sort.column, sort.direction, column)
        let newDirection = 'asc'

        if(sort.column == column){
            newDirection = sort.direction === 'asc' ? "desc" : 'asc'
        }

        // newDirection = sort.direction === "asc" ? "desc" : "asc"
        let newSort = {
            column: column,
            direction: newDirection,
        }
        setSort(newSort)
        handleFilterFiles(null, newSort)
    }

    const renderColumnHeaderTitle = (title, key = '') => {
        return <ColumnHeaderTitle title={title} handleSortChange={handleSortBatchChange} sort={sort} replacementTitle={key} />
    }

    const renderSearchOutlinedButton = (filterKey) => {
        return <SearchOutlined style={{ fontSize: "16px", color: tableFiltersApplied(filterKey, filters) ? "#1890ff" : undefined }} />
    }

    // const showExportConfirm = useExportConfirmationModal()
    // const handleRegenrate = async (id) => {
    //     showExportConfirm(regenerateQBCsv(id))
    // }

    const regenerateQBCsv = async (id) => {
        notification.open({
            message: "Quickbook file regeneration has started.",
            duration: 2,
        })

        await dispatch(regenerateQuickBookFile(id));
        await new Promise(resolve => setTimeout(resolve, 500)); // 500ms delay
        await dispatch(fetchGlobalBatchFiles(pagination, sort));
    }

    // console.log(globalBatchFiles)

    return (
        <div className="muc-table card" style={{ marginBottom: "50px" }}>
            <div className="card-body">
                <div>
                    <div className="card-body-heading">
                        <div>
                            <div className="badged-header">
                                <h2>Global Batch File History</h2>
                                <div className="badge">{total}</div>
                            </div>
                        </div>
                    </div>
                    <Skeleton active loading={loading}>
                        <Table
                            dataSource={globalBatchFiles}
                            pagination={{ ...pagination, showSizeChanger: true, total: total, pageSizeOptions: [10, 20, 50, 100, 1000] }}
                            onChange={handleTableChange}
                            rowKey={(r) => r.id}>
                            <Table.Column
                                key="file_name"
                                dataIndex="file_name"
                                title="File Name"
                                render={(text, record) => {
                                    const needsWarning = text.includes("Quickbook") && record.batch_file_size == 640
                                    return (
                                        <>
                                            {needsWarning && <WarningChip message={"Quickbook file is blank. Please regenerate"} />}
                                            {text}
                                        </>
                                    )
                                }}
                            />
                            <Table.Column
                                key="created_at"
                                dataIndex="created_at"
                                title={renderColumnHeaderTitle("created_at", "Date Created")}
                                render={(text, record, index) => moment(text).format("L")}
                                filterDropdown={({ visible }) => (
                                    <ColumnDateFilter
                                        handleFetchInitial={handleFetchInitialFiles}
                                        filterAction={filterFiles}
                                        filterColumn={"created_at"}
                                        filterType={"created_at"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={setFilters}
                                        sort={sort}
                                        setSort={setSort}
                                        visible={visible}
                                        // disabledDate={disabledDate}
                                    />
                                )}
                                filterIcon={renderSearchOutlinedButton("created_at_lteq" || "created_at_gteq")}
                            />
                            <Table.Column
                                key="created_at"
                                dataIndex="created_at"
                                title="Time Created"
                                render={(text, record, index) =>
                                    // moment(text).format("LT")
                                    new Date(text).toLocaleString("en-US", {
                                        timeZone: "America/New_York",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        hour12: true,
                                    })
                                }
                            />
                            <Table.Column
                                key="export_type"
                                dataIndex="export_type"
                                title={renderColumnHeaderTitle("export_type", "Exported As:")}
                                filterDropdown={({ visible }) => (
                                    <ColumnSearchFilter
                                        handleFetchInitial={handleFetchInitialFiles}
                                        filterAction={filterFiles}
                                        filterColumn={"export_type_cont"}
                                        filterType={"export_type_cont"}
                                        filters={filters}
                                        pagination={pagination}
                                        setFilters={setFilters}
                                        sort={sort}
                                        setSort={setSort}
                                        visible={visible}
                                        // disabledDate={disabledDate}
                                    />
                                )}
                                filterIcon={renderSearchOutlinedButton("export_type_cont")}
                            />
                            <Table.Column
                                title="File"
                                key="download"
                                // LoadingOutlined icon when new file is generating
                                render={(text, record) => {
                                    if (!record.batch_file_url) {
                                        return null
                                    }
                                    
                                    if(record.file_name.includes("Quickbook") && record.batch_file_size == 640){
                                        return (
                                            <Tooltip title="Regenerate File">
                                                <SyncOutlined style={{ color: "#4088fb" }} onClick={() => regenerateQBCsv(record.id)} />
                                            </Tooltip>
                                        )
                                    }
                                    else{
                                        return (
                                            <a href={record.batch_file_url} target="_blank" rel="noreferrer">
                                                <Tooltip title="View File">
                                                    <FileTextOutlined />
                                                </Tooltip>
                                            </a>
                                        )
                                    }
                                }}
                            />
                        </Table>
                    </Skeleton>
                </div>
            </div>
        </div>
    )
}
